import firebase from 'firebase.js';
import { getArrayFromDataframe, getHexSerialNumber } from 'utils/hdrainHelper';
import config from 'config';
import { removeEmpty } from 'utils';
import {
  useFormatDateTime,
  useFormatDateTimeSecond,
  useFormatDifferenceTime,
} from 'hooks';

export const getStationData = (serialNumber) => {
  if (!serialNumber) {
    return new Promise((resolve) => {
      resolve(null);
    });
  }
  // Convert decimal id into hexadecimal id
  const hexStationId = getHexSerialNumber(serialNumber);

  if (config.isLocal) {
    firebase.functions().useEmulator('localhost', 5001);
  }
  const callable = firebase.functions().httpsCallable('v0');

  const params = {
    hexStationId,
  };

  return callable(params).then((result) => {
    const data = { signal: {}, status: {}, locales: {} };
    const responseData = result.data;
    if (
      responseData &&
      responseData.signal &&
      responseData.signal[hexStationId]
    ) {
      data.signal[hexStationId] = getArrayFromDataframe(
        responseData.signal[hexStationId]
      );
    }
    if (
      responseData &&
      responseData.status &&
      responseData.status[hexStationId]
    ) {
      data.status[hexStationId] = getArrayFromDataframe(
        responseData.status[hexStationId]
      );
    }
    if (
      responseData &&
      responseData.locales &&
      responseData.locales[hexStationId]
    ) {
      data.locales[hexStationId] = getArrayFromDataframe(
        responseData.locales[hexStationId]
      );
    }
    return data;
  });
};

export const getLastStationsData = (serialNumbers) => {
  // Convert decimal id into hexadecimal id
  const hexStationIds = serialNumbers
    .map((serialNumber) => {
      return getHexSerialNumber(serialNumber);
    })
    .filter((serialNumber) => {
      return serialNumber;
    });

  if (hexStationIds && hexStationIds.length === 0) {
    return new Promise((resolve) => {
      resolve({});
    });
  }

  const onlyLast = true;
  if (config.isLocal) {
    firebase.functions().useEmulator('localhost', 5001);
  }
  const callable = firebase.functions().httpsCallable('v0');

  const params = {
    hexStationIds,
    onlyLast,
  };

  return callable(params)
    .then((result) => {
      const { data } = result;
      // console.log('getLastStationsData', { data });
      return data;
    })
    .catch(() => {
      // console.log(e);
      return {};
    });
};

export const getInstallationData = (serialNumber) => {
  if (!serialNumber) {
    return new Promise((resolve) => {
      resolve(null);
    });
  }
  // Convert decimal id into hexadecimal id
  const hexStationId = getHexSerialNumber(serialNumber);

  if (config.isLocal) {
    firebase.functions().useEmulator('localhost', 5001);
  }
  const callable = firebase.functions().httpsCallable('getInstallationData');

  const params = {
    hexStationId,
    roundTo30s: true,
  };

  return callable(params).then((result) => {
    const data = { signal: {}, status: {}, locales: {} };
    const responseData = result.data;
    if (
      responseData &&
      responseData.signal &&
      responseData.signal[hexStationId]
    ) {
      data.signal[hexStationId] = getArrayFromDataframe(
        responseData.signal[hexStationId]
      );
    }
    if (
      responseData &&
      responseData.status &&
      responseData.status[hexStationId]
    ) {
      data.status[hexStationId] = getArrayFromDataframe(
        responseData.status[hexStationId]
      );
    }
    if (
      responseData &&
      responseData.locales &&
      responseData.locales[hexStationId]
    ) {
      data.locales[hexStationId] = getArrayFromDataframe(
        responseData.locales[hexStationId]
      );
    }
    return data;
  });
};

export const getStationsUsersGroupsByUser = () => {
  if (config.isLocal) {
    firebase.functions().useEmulator('localhost', 5001);
  }
  const callable = firebase
    .functions()
    .httpsCallable('getStationsUsersGroupByUser');

  return callable()
    .then((result) => {
      const { data } = result;
      if (data && data.httpErrorCode) {
        return [];
      }
      if (data && data.stationsUsersGroups) {
        return data.stationsUsersGroups;
      }
      return [];
    })
    .catch((e) => {
      console.error(e); // eslint-disable-line no-console
      return [];
    });
};

export const getStationsByUser = () => {
  if (config.isLocal) {
    firebase.functions().useEmulator('localhost', 5001);
  }
  const callable = firebase.functions().httpsCallable('getStationsByUser');

  return callable()
    .then((result) => {
      const { data } = result;
      if (data && data.httpErrorCode) {
        return [];
      }
      if (data && data.stations && data.stationsUsersGroups) {
        return data.stations;
      }
      return [];
    })
    .catch((e) => {
      console.error(e); // eslint-disable-line no-console
      return [];
    });
};

export const updateStationByUser = (id, station) => {
  const newValues = removeEmpty({ ...station });
  newValues.updatedAt = new Date().toISOString();
  const params = {
    stationId: id,
    station: newValues,
  };
  if (config.isLocal) {
    firebase.functions().useEmulator('localhost', 5001);
  }
  const callable = firebase.functions().httpsCallable('updateStationByUser');

  return callable(params)
    .then((result) => {
      const { data } = result;
      if (data && data.httpErrorCode) {
        return null;
      }

      return data;
    })
    .catch((e) => {
      console.error(e); // eslint-disable-line no-console
      return { success: false, error: e };
    });
};

export const disableUser = (userId) => {
  if (config.isLocal) {
    firebase.functions().useEmulator('localhost', 5001);
  }
  const params = {
    userId,
  };
  const callable = firebase.functions().httpsCallable('disableUser');

  return callable(params)
    .then((result) => {
      const { data } = result;
      if (data && data.httpErrorCode) {
        return false;
      }
      if (data && data.success) {
        return true;
      }
      return false;
    })
    .catch((e) => {
      console.error(e); // eslint-disable-line no-console
      return [];
    });
};

//* ****************************************************************
// Fonction for temporary rvb3 data - Add february 2022

// Get RBV3 data for all available stations
export const getRbv3sStations = () => {
  if (config.isLocal) {
    firebase.functions().useEmulator('localhost', 5001);
  }

  const callable = firebase.functions().httpsCallable('rbv3sData');

  return callable()
    .then((response) => {
      return response.data.stations.map((station) => ({
        sn: station.sn,
        cnx: useFormatDateTime(station.cnx),
        lt: `${useFormatDateTimeSecond(station.lt)} (${useFormatDifferenceTime(
          station.lt
        )})`,
        vrs: station.vrs ? station.vrs : '-',
      }));
    })
    .catch((e) => {
      console.error(e); // eslint-disable-line no-console
      return [];
    });
};

// Get RBV3 data for one station
export const getRbv3Station = (index) => {
  if (config.isLocal) {
    firebase.functions().useEmulator('localhost', 5001);
  }

  const params = {
    index,
  };

  const callable = firebase.functions().httpsCallable('rbv3Data');

  return callable(params)
    .then((response) => {
      let rbv3StationData = {};

      if (response.data.success === false) {
        rbv3StationData = {
          cnx: 'n.c.',
          cnxDiff: 'n.c.',
          lt: 'n.c.',
          ltDiff: 'n.c.',
          vbat: 'n.c.',
          crFwVersion: 'n.c.',
          pvFwVersion: 'n.c.',
          ftFwVersion: 'n.c.',
          lbFwVersion: 'n.c.',
        };
      } else {
        rbv3StationData = {
          cnx: useFormatDateTimeSecond(response.data.cnx),
          cnxTimestamp: response.data.cnx,
          cnxDiff: useFormatDifferenceTime(response.data.cnx),
          lt: useFormatDateTimeSecond(response.data.lt),
          ltTimestamp: response.data.lt,
          ltDiff: useFormatDifferenceTime(response.data.lt),
          vbat: response.data.vbat,
          crFwVersion: response.data.firmwares.crFwVersion,
          pvFwVersion: response.data.firmwares.pvFwVersion,
          ftFwVersion: response.data.firmwares.ftFwVersion,
          lbFwVersion:
            response.data.firmwares.lbFwVersion === 4294967295
              ? 'none'
              : response.data.firmwares.lbFwVersion,
        };
      }
      return rbv3StationData;
    })
    .catch((e) => {
      console.error(e); // eslint-disable-line no-console
      return [];
    });
};

// Reboot a RBV3 station
// export const rebootRbv3Station = (index) => {
//   if (config.isLocal) {
//     firebase.functions().useEmulator('localhost', 5001);
//   }

//   const params = {
//     index,
//   };

//   const callable = firebase.functions().httpsCallable('rbv3Reboot');

//   return callable(params)
//     .then((response) => {
//       console.log("Reboot in hdrainClient ok");
//       console.log({response}); // response.data.success === true/false
//       if (response.data.success){
//         return window.confirm(`Station ${index} rebooted`);
//       }
//       return alert(`An error has occured`);

//     })
//     .catch((e) => {
//       console.error(e); // eslint-disable-line no-console
//       return [];
//     });
// };
